import React, { useEffect, useState } from "react";
import { isMobile, isMobileOnly, isTablet } from "react-device-detect";
import { SavedWidgetCanvasLayout, SavedWidgetLayout } from "@lifeat/shared/data-models/widget/WidgetDataModels";
import { FocusModeSidebarContentType } from "@lifeat/shared/system/AppModeTypes";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import useLocalStorage from "@hooks/useLocalStorage";
import { LocalStorageKeys, removeItemFromLocalStorage } from "@service/LocalStorageService";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import WidgetCanvasContext, { WidgetCanvasContextType } from "../WidgetCanvasContext";
import WidgetDataManager, { WidgetEntryType, widgetData } from "../WidgetDataManager";
import WidgetToolbar from "./Toolbar/WidgetToolbar";
import WidgetToolbarV1 from "./Toolbar/WidgetToolbarV1";
import { WidgetProps } from "./Widget/Widget";
import "./Workspace.scss";

const INIT_MOBILE_POS_CENTERED = {
    x: window.innerWidth - 370, // TODO: clean up for mobile. 370 is basically centered on iPhone 11 pro
    y: window.innerHeight / 4,
};

export const WIDGETS_STARTING_Z_IDX = 100; // needs to match $widget-canvas-z-idx in LifeAtApp.scss

interface WorkspaceProps {
    readonly sidebarContent: FocusModeSidebarContentType;
    readonly sidebarOpen: boolean;
    readonly toggleSpacesSidebar: () => void;
    readonly toggleCalendarSidebar: () => void;
    readonly toggleSoundboardSidebar: () => void;
    readonly toggleAppsSidebar: () => void;
}
export default function Workspace(props: WorkspaceProps) {
    const { user, userData }: UserContextType = useUserContext();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();

    const widgetDataManager = WidgetDataManager();

    // Widget keys that all users have access to as soon as they access the app.
    const defaultWidgetKeys = ["TimerWidget"];
    // const defaultWidgetKeys = Object.keys(widgetData)
    //     .filter((key: string) => {
    //         const widgetEntry = widgetData[key];
    //         if (!widgetEntry.premium && !widgetEntry.requireUserAccount) {
    //             return key;
    //         }
    //     }) || [];

    const permanentWidgetKeys =
        widgetDataManager.getWidgetKeys((widgetEntry: WidgetEntryType): boolean => widgetEntry.isStatic) || [];

    // Start this at 1 above the starting index, so the first widget that's clicked on will
    // be lower than this count and get updated.
    const [activeWidgetZIndex, setActiveWidgetZIndex] = useState<number>(WIDGETS_STARTING_Z_IDX + 1);

    // The user's widget layout from the last time they used the app.
    const [savedWidgetLayouts, setSavedWidgetLayouts] = useLocalStorage<SavedWidgetCanvasLayout>(
        LocalStorageKeys.SavedWidgetLayoutInfo,
        {},
    );

    // Open widgets should consist of static widgets AND any widgets that were open the last
    // time the user accessed the app (and were saved to localstorage).
    const [openWidgetKeys, setOpenWidgetKeys] = useState<Set<string>>(new Set());
    useEffect(() => {
        // If widget layout has been saved, init the UI to that layout.
        if (savedWidgetLayouts !== undefined && Object.keys(savedWidgetLayouts).length > 0) {
            setOpenWidgetKeys(
                new Set(
                    permanentWidgetKeys.concat(
                        Object.entries(savedWidgetLayouts).map(
                            ([widgetKey, widgetLayout]: [string, SavedWidgetLayout]) => {
                                if (
                                    widgetLayout !== undefined &&
                                    widgetLayout.wasOpen &&
                                    widgetLayout.xPos &&
                                    widgetLayout.yPos
                                ) {
                                    return widgetKey;
                                }
                                return undefined;
                            },
                        ),
                    ),
                ),
            );
        } else {
            // If the user doesn't have a saved layout and is on desktop/tablet only,
            // open all of the default widget keys.
            if (!isMobile && !isTablet) {
                setOpenWidgetKeys(new Set(defaultWidgetKeys));
            }
        }
    }, []);

    // TODO: need to abstract this better along with the widget keys (maybe put into enum?)
    const getWidgetDefaultPosition = (widgetKey: string) => {
        const widgetKeyPositionMap: { [id: string]: any } = {
            MediaWidget: isMobile && !isTablet ? INIT_MOBILE_POS_CENTERED : { x: window.innerWidth - 300, y: 56 },
            TaskListWidget: isMobile && !isTablet ? { x: (window.innerWidth - 360) / 2, y: 60 } : { x: 370, y: 250 },
            NotesWidget: isMobile && !isTablet ? INIT_MOBILE_POS_CENTERED : { x: window.innerWidth - 360, y: 300 },
            TimerWidget: isMobile && !isTablet ? { x: (window.innerWidth - 360) / 2, y: 60 } : { x: 370, y: 56 },
            SoundboardWidget:
                isMobile && !isTablet ? INIT_MOBILE_POS_CENTERED : { x: window.innerWidth - 316 - 360, y: 213 },
            FortuneWidget:
                isMobile && !isTablet ? INIT_MOBILE_POS_CENTERED : { x: window.innerWidth - 316 - 360, y: 501 },
            FocusMonitoringWidget: { x: 0, y: 0 },
        };
        return widgetKeyPositionMap[widgetKey];
    };

    const handleToggleWidgetVisibility = (isVisible: boolean, key: string): void => {
        if (isVisible) {
            openWidget(key);
        } else {
            closeWidget(key);
        }
    };

    // Build the Widget components to be displayed using the data from the manager.
    const widgetComponents = Object.entries(widgetData).map(([key, widgetEntry]: [string, WidgetEntryType], idx) => {
        let WidgetComponent: React.ComponentType<Omit<WidgetProps, "isStatic" | "footerAutoHide" | "lightMode">> =
            widgetEntry.component; // Widget.tsx component

        // Skip any processing for the Spaces Sidebar (which is technically a static widget)
        if (!WidgetComponent || key === "SpacesWidget" || key === "CalendarWidget") {
            return undefined;
        }

        return (
            <WidgetComponent
                key={key}
                amplitudeEventSource="focus-mode"
                name={widgetEntry.name}
                isUserAuthenticated={!!user && !!userData}
                unauthenticatedUserCallback={() => setSignInView(SignUpCTASource.TOOLBAR)}
                showTitle={widgetEntry.showTitle}
                isOpen={openWidgetKeys.has(key)}
                initialPos={getWidgetDefaultPosition(key)} // TODO: not used if position was saved, need to remove or rename
                isPositionFixed={widgetEntry.isPositionFixed}
                isResizable={widgetEntry.isResizable}
                openWidget={() => handleToggleWidgetVisibility(true, key)}
                closeWidget={() => handleToggleWidgetVisibility(false, key)}
                savedLayout={savedWidgetLayouts[key]}
                updateSavedLayout={(widgetLayout: SavedWidgetLayout) =>
                    setSavedWidgetLayouts({ ...savedWidgetLayouts, [key]: widgetLayout })
                }
            />
        );
    });

    const openWidget = (key: string): void => {
        let updatedSavedLayout = { ...savedWidgetLayouts[key], wasOpen: true };

        // If widget is being opened for the first time since adding the saved layout
        // functionality, we need to update the state of the layout with the default coordinates.
        if (
            savedWidgetLayouts[key] === undefined ||
            savedWidgetLayouts[key].xPos === undefined ||
            savedWidgetLayouts[key].yPos === undefined
        ) {
            const defaultPos = getWidgetDefaultPosition(key);
            updatedSavedLayout = { ...updatedSavedLayout, xPos: defaultPos.x, yPos: defaultPos.y };
        }

        setSavedWidgetLayouts({ ...savedWidgetLayouts, [key]: updatedSavedLayout });

        const newOpenWidgets = new Set(openWidgetKeys).add(key);
        setOpenWidgetKeys(newOpenWidgets);
    };

    const closeWidget = (key: string): void => {
        // The x/y coordinate should already be set for the saved layout at this point.
        setSavedWidgetLayouts({ ...savedWidgetLayouts, [key]: { ...savedWidgetLayouts[key], wasOpen: false } });

        if (permanentWidgetKeys.includes(key)) {
            return;
        }
        const tempOpenWidgetKeys = new Set(openWidgetKeys);
        tempOpenWidgetKeys.delete(key);
        setOpenWidgetKeys(tempOpenWidgetKeys);
    };

    const toggleWidget = (key: string): void => {
        const isOpen = openWidgetKeys.has(key);
        if (isOpen) {
            closeWidget(key);
        } else {
            // Only have 1 widget open at a time on mobile.
            if (isMobile && !isTablet) {
                closeSidebarAndResetWidgets();
            }
            openWidget(key);
        }
    };

    const closeSidebarAndResetWidgets = () => {
        openWidgetKeys.clear();
        setSavedWidgetLayouts({});
        removeItemFromLocalStorage(LocalStorageKeys.SavedWidgetLayoutInfo);
    };

    const toggleSpacesSidebar = () => {
        // The sidebar counts as a widget, so we close all other widgets when opening the sidebar
        // if the user is on mobile (because only 1 widget should be open at a time on mobile).
        if (isMobile && !isTablet && !props.sidebarOpen) {
            closeSidebarAndResetWidgets(); // also resets widget positions so they're always centered for mobile
        }
        props.toggleSpacesSidebar();
    };

    const toggleCalendarSidebar = () => {
        // The sidebar counts as a widget, so we close all other widgets when opening the sidebar
        // if the user is on mobile (because only 1 widget should be open at a time on mobile).
        if (isMobile && !isTablet && !props.sidebarOpen) {
            closeSidebarAndResetWidgets(); // also resets widget positions so they're always centered for mobile
        }
        props.toggleCalendarSidebar();
    };

    const toggleSoundboardSidebar = () => {
        // The sidebar counts as a widget, so we close all other widgets when opening the sidebar
        // if the user is on mobile (because only 1 widget should be open at a time on mobile).
        if (isMobile && !isTablet && !props.sidebarOpen) {
            closeSidebarAndResetWidgets(); // also resets widget positions so they're always centered for mobile
        }
        props.toggleSoundboardSidebar();
    };

    const toggleAppSidebar = () => {
        // The sidebar counts as a widget, so we close all other widgets when opening the sidebar
        // if the user is on mobile (because only 1 widget should be open at a time on mobile).
        if (isMobile && !isTablet && !props.sidebarOpen) {
            closeSidebarAndResetWidgets(); // also resets widget positions so they're always centered for mobile
        }
        props.toggleAppsSidebar();
    };

    const widgetCanvasContextVal: WidgetCanvasContextType = {
        activeWidgetZIndex: activeWidgetZIndex,
        setActiveWidgetZIndex: (zIdx: number) => setActiveWidgetZIndex(zIdx),
    };

    return (
        <WidgetCanvasContext.Provider value={widgetCanvasContextVal}>
            <div id="widgetCanvas" className={"workspace"}>
                <span className={`dynamic-element`}>
                    {isMobileOnly ? (
                        <WidgetToolbarV1
                            widgetData={widgetData}
                            openWidgetKeys={openWidgetKeys}
                            toggleWidget={toggleWidget}
                            sidebarOpen={props.sidebarOpen}
                            sidebarContent={props.sidebarContent}
                            toggleSpacesSidebar={toggleSpacesSidebar}
                            toggleCalendarSidebar={toggleCalendarSidebar}
                            toggleSoundboardSidebar={toggleSoundboardSidebar}
                            closeAndResetAllWidgets={closeSidebarAndResetWidgets}
                        />
                    ) : (
                        <WidgetToolbar
                            widgetData={widgetData}
                            openWidgetKeys={openWidgetKeys}
                            toggleWidget={toggleWidget}
                            sidebarOpen={props.sidebarOpen}
                            sidebarContent={props.sidebarContent}
                            toggleSpacesSidebar={toggleSpacesSidebar}
                            toggleCalendarSidebar={toggleCalendarSidebar}
                            toggleSoundboardSidebar={toggleSoundboardSidebar}
                            toggleAppsSidebar={toggleAppSidebar}
                            closeAndResetAllWidgets={closeSidebarAndResetWidgets}
                        />
                    )}
                </span>

                {widgetComponents}
            </div>
        </WidgetCanvasContext.Provider>
    );
}
